<template>
  <div
    v-if="label"
    class="field"
  >
    <label class="label mb-2">
      <h3>{{ label }}</h3>
    </label>
    <p
      v-if="hasHelpText"
      class="help-text"
      v-html="finalHelpText"
    ></p>
    <div class="ui-type-area">
      <editor
        v-model="model"
        :disabled="isDisabled"
        :init="tinymceSettings"
      />
    </div>
    <div class="info-wrapper">
      <span
        v-if="countChar || maxlength"
        class="text-count has-text-right mt-2"
      >
        {{ characterCounter }}
      </span>
    </div>
  </div>
</template>

<script>
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/plugins/link';
import 'tinymce/plugins/table';
import 'tinymce/plugins/paste';
import 'tinymce/themes/silver';

import Editor from '@tinymce/tinymce-vue';
import commonProperties from './common-properties';

require.context(
  '!file-loader?name=[path][name].[ext]&context=node_modules/tinymce&outputPath=js!tinymce/skins',
  true,
  /.*/,
);

export default {
  name: 'Html',
  components: {
    Editor,
  },
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    wrapWithParagraph: {
      type: Boolean,
      default: true,
    },
    maxlength: {
      type: Number,
      default: null,
    },
    countChar: {
      type: Boolean,
      default: false,
    },
    default: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tinymceSettings: {
        deprecation_warnings: false, // revise forced_root_block option soon
        language: 'pt_BR',
        language_url: '/tinymce-langs/pt_BR.js',
        branding: false,
        menubar: false,
        plugins: 'link table paste',
        browser_spellcheck: true,
        contextmenu: false,
        paste_as_text: true,
        resize: false,
        statusbar: false,
        toolbar_mode: 'sliding',
        toolbar:
          'bold italic forecolor backcolor | link | table | alignleft aligncenter alignright | fontselect',
        forced_root_block: this.wrapWithParagraph ? 'p' : false,
        font_formats:
          'Arial=arial,helvetica,sans-serif; Verdana=verdana,geneva,sans-serif; Tahoma=tahoma,arial,helvetica,sans-serif; Trebuchet MS=trebuchet ms,geneva,sans-serif; Times New Roman=times new roman,times,serif; Georgia=georgia,palatino,serif; Garamond=garamond,times new roman,serif; Courier New=courier new,courier,monospace; Brush Script MT=brush script mt,cursive;',
      },
    };
  },
  computed: {
    characterCounter() {
      const max = this.maxlength ? `/${this.maxlength}` : '';
      const doc = new DOMParser().parseFromString(this.model, 'text/html');
      const counter = doc.body.textContent.trim().length;

      return `${counter}${max} caracteres`;
    },
  },
  created() {
    this.initTinyMCE();
  },
  methods: {
    initTinyMCE() {
      if (this.maxlength) {
        const limit = this.maxlength;

        this.tinymceSettings.setup = (editor) => {
          editor.on('keypress', (event) => {
            const content = editor.getContent({ format: 'text' });

            if (content.length >= limit) {
              event.preventDefault();
            }
          });

          // Caso de colar o texto
          editor.on('change', (event) => {
            const content = editor.getContent({ format: 'text' });

            if (content.length > limit) {
              const clippedText = content.substring(0, limit);

              editor.setContent(clippedText);
              editor.save(); // atualiza o model

              event.preventDefault();
            }
          });

          // Caso de usar CTRL+Z após colar texto maior
          editor.on('beforeExecCommand', (event) => {
            const content = editor.getContent({ format: 'text' });

            if (event.command === 'Undo' && content.length >= limit) {
              event.preventDefault();
            }
          });
        };
      }
    },
  },
};
</script>

<style scoped lang="scss">
h3 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}

::v-deep {
  .tox-tinymce {
    min-height: 200px;
    height: 100% !important;
    border-radius: 4px;
  }
}

.info-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .text-count {
    width: 100%;
    font-size: $size-smaller;
    color: $gray-50;
    font-weight: $lb-medium;
  }
}
</style>
